<template>
	<CDialog
		:visible="model != null"
		title="Confirm Billing Address"
		:custom-class="model?.AllowOriginal && !addressesAreSame ? 'dialog--medium' : 'dialog--tiny'"
		data-testid="confirm-billing-address"
		@done="$emit('cancel')"
	>
		<template v-if="model != null">
			<CCallout v-if="!model.AddressLocated" type="warning">
				Your address could not be located. Please verify that it was entered correctly.
			</CCallout>
			<div v-if="model.AllowOriginal && addressesAreSame">
				<p class="mb-2 font-bold">Address</p>
				<div class="text-3">
					<p>{{ model.OriginalAddress.Line1 }}</p>
					<p>{{ model.OriginalAddress.Line2 }}</p>
					<p>{{ model.OriginalAddress.Line3 }}</p>
					<p>{{ model.OriginalAddress.CityStatePostalCode }}</p>
					<p>{{ model.OriginalAddress.Country }}</p>
				</div>
			</div>

			<RadioGroup
				v-else-if="model.AllowOriginal"
				v-model="choice"
				class="grid b7:grid-cols-2 gap-4 c-radio-fancy text-3"
			>
				<RadioButton
					class="p-2 pointer"
					value="provided"
					label="provided"
				>
					<div class="inline-flex flex-col ml-2">
						<span class="font-bold">Provided Address</span>
						<div class="mt-2">
							<p>{{ model.OriginalAddress.Line1 }}</p>
							<p>{{ model.OriginalAddress.Line2 }}</p>
							<p>{{ model.OriginalAddress.Line3 }}</p>
							<p>{{ model.OriginalAddress.CityStatePostalCode }}</p>
							<p>{{ model.OriginalAddress.Country }}</p>
						</div>
					</div>
				</RadioButton>

				<RadioButton
					class="p-2 pointer"
					value="updated"
					label="updated"
				>
					<div class="inline-flex flex-col ml-2">
						<span class="font-bold">Updated Address</span>
						<div class="mt-2">
							<p>{{ model.ValidatedAddress.Line1 }}</p>
							<p>{{ model.ValidatedAddress.Line2 }}</p>
							<p>{{ model.ValidatedAddress.Line3 }}</p>
							<p>{{ model.ValidatedAddress.CityStatePostalCode }}</p>
							<p>{{ model.ValidatedAddress.Country }}</p>
						</div>
					</div>
				</RadioButton>
			</RadioGroup>

			<div v-else>
				<CCallout v-if="model.AddressLocated" type="info">
					Your address has been updated. Please confirm that it is correct.
				</CCallout>

				<div class="text-3">
					<p class="mb-2 font-bold">Updated Address</p>

					<p>{{ model.ValidatedAddress.Line1 }}</p>
					<p>{{ model.ValidatedAddress.Line2 }}</p>
					<p>{{ model.ValidatedAddress.Line3 }}</p>
					<p>{{ model.ValidatedAddress.CityStatePostalCode }}</p>
					<p>{{ model.ValidatedAddress.Country }}</p>
				</div>
			</div>
		</template>

		<template #footer>
			<button
				class="c-modal-button c-modal-button-cancel button--secondary"
				@click="$emit('cancel')"
			>
				Edit
			</button>
			<CStatusButton
				:status="status"
				class="c-modal-button button--primary"
				@click="status = 'validating'; $emit('confirm', choice === 'updated' ? model.ValidatedAddress : model.OriginalAddress)"
			>
				Confirm
			</CStatusButton>
		</template>
	</CDialog>
</template>

<script lang="ts">
	import { ref, defineComponent, type PropType, computed } from 'vue';
	import { RadioGroup, RadioButton } from '@cognitoforms/element-ui';

	import type { Address } from '@cognitoforms/types/server-types/model/address';
	import type { BillingAddressConfirmModel } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/billing-address-confirm-model';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CStatusButton, { type ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import CCallout from 'src/components/content/Callout.vue';

	export default defineComponent({
		components: {
			RadioGroup,
			RadioButton,
			CCallout,
			CDialog,
			CStatusButton
		},
		props: {
			model: {
				type: Object as PropType<BillingAddressConfirmModel | null>,
				default: null
			}
		},
		emits: {
			cancel() { return true; },
			confirm(payload: Address) { return payload != null; }
		},
		setup(props) {
			const status = ref<ButtonStatus>('default');
			const choice = ref<'provided' | 'updated'>('updated');

			function normalizeString(val: string | null | undefined) {
				return val || '';
			}
			return {
				status,
				choice,
				addressesAreSame: computed(() => {
					const a = props.model?.OriginalAddress;
					const b = props.model?.ValidatedAddress;
					if (!a || !b) return false;
					return (
						normalizeString(a.Line1) === normalizeString(b.Line1) &&
						normalizeString(a.Line2) === normalizeString(b.Line2) &&
						normalizeString(a.Line3) === normalizeString(b.Line3) &&
						normalizeString(a.City) === normalizeString(b.City) &&
						normalizeString(a.State) === normalizeString(b.State) &&
						normalizeString(a.PostalCode) === normalizeString(b.PostalCode) &&
						normalizeString(a.CountryCode) === normalizeString(b.CountryCode)
					);
				})
			};
		},
		watch: {
			model() {
				this.status = 'default';
				this.choice = 'updated';
			}
		}
	});
</script>